import React, { useCallback, useEffect } from 'react';
import { FlatList, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Asset } from 'expo-asset';
import { t } from 'i18next';
import useGiphy, { GiphyImage } from '../../hooks/useGiphy';
import { _compressImage, PickPhotoOrDocumentResult } from '../../services/utils';
import AppText from '../atomic/AppText';
import { PRIMARY_COLOR, WHITE } from '../../styles/appColor';
import { FONT_FAMILY_DEFAULT } from '../../styles/globalStyle';
import { ActivityIndicator } from 'react-native';
import Image from '../atomic/Image';

export type GiphyPickerProps = {
  searchQuery: string;
  setPhoto: (photo: PickPhotoOrDocumentResult) => void;
  onDone: () => void;
};

export default function GiphyPicker({ searchQuery, setPhoto, onDone }: GiphyPickerProps) {
  const { images, isLoading, searchImages, fetchNext } = useGiphy();

  // Initial search with an empty query
  useEffect(() => {
    if (searchImages) {
      searchImages('');
    }
  }, [searchImages]);

  // Search when query is updated, three characters minimum
  useEffect(() => {
    if (searchImages && (searchQuery.length > 2 || searchQuery.length === 0)) {
      searchImages(searchQuery);
    }
  }, [searchQuery, searchImages]);

  const handleImagePressed = useCallback(
    (item: GiphyImage) => async () => {
      if (Platform.OS === 'web') {
        const [result] = await Asset.loadAsync((item.file || item.uri) as any);
        let blob = await fetch(result.uri).then((r) => r.blob());
        let metadata = {
          type: 'image/png',
        };
        let file = new File([blob], 'mypicture.png', { ...metadata, lastModified: Date.now() });
        (file as any).uri = result.uri;

        setPhoto(file as any);
      } else {
        const [result] = await Asset.loadAsync(item.uri);

        var newPhoto = {
          name: 'mypicture.gif',
          fileName: 'mypicture.gif',
          type: 'image/gif',
          height: result.height,
          width: result.width,
          uri: result.uri,
        };

        setPhoto(newPhoto);
      }
      onDone();
    },
    []
  );

  return (
    <View style={styles.container}>
      <View style={styles.listContainer}>
        {isLoading ? (
          <ActivityIndicator color={PRIMARY_COLOR} size="large" />
        ) : (
          <FlatList
            numColumns={2}
            contentContainerStyle={styles.listContentContainer}
            onEndReached={fetchNext}
            data={images}
            keyExtractor={(item) => item.id}
            refreshing={isLoading}
            ListEmptyComponent={<AppText style={styles.listEmptyLabel}>{t('no_image_found')}</AppText>}
            renderItem={({ item }) => (
              <TouchableOpacity style={styles.listItemContainer} onPress={handleImagePressed(item)}>
                <Image style={styles.listItemImage} source={item.file || { uri: item.uri }} />
              </TouchableOpacity>
            )}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContainer: { flex: 1 },
  listContentContainer: {
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  listEmptyLabel: { margin: 10 },
  listItemContainer: {
    backgroundColor: 'transparent',
    margin: 5,
  },
  listItemImage: {
    width: 100,
    height: 100,
  },
  listItemImageCaption: { color: WHITE, fontFamily: FONT_FAMILY_DEFAULT.bold, fontSize: 8 },
  underline: { textDecorationLine: 'underline' },
});
